import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { adminApi } from "../services/api";
import { useI18n } from "@expertloop/i18n/react";

interface UserSummary {
	id: string;
	email: string;
	firstName: string;
	lastName: string;
	displayName?: string;
	role: string;
	isExpert: boolean;
	isClient: boolean;
	emailVerified: boolean;
	authProvider: string;
	stripeOnboardingCompleted?: boolean;
	averageRating?: number;
	totalRatings?: number;
	subscriptionTier?: string;
	subscriptionStatus?: string;
	// URL publique de la boutique, fournie par l'API pour les seuls experts
	storeUrl?: string;
	createdAt: string;
	// Guest-specific fields
	chatCount?: number;
	lastActivity?: string;
}

interface UsersResponse {
	users: UserSummary[];
	total: number;
	page: number;
	pageSize: number;
	totalPages: number;
}

export function UsersPage() {
	const { t } = useI18n();
	const [users, setUsers] = useState<UserSummary[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [page, setPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [total, setTotal] = useState(0);
	const [role, setRole] = useState("all");
	const [search, setSearch] = useState("");
	const [searchInput, setSearchInput] = useState("");

	useEffect(() => {
		const fetchUsers = async () => {
			setLoading(true);
			try {
				const params = new URLSearchParams({
					page: page.toString(),
					pageSize: "20",
					role,
				});
				if (search) params.append("search", search);

				const data = await adminApi.get<UsersResponse>(
					`/admin/users?${params}`,
				);
				setUsers(data.users);
				setTotalPages(data.totalPages);
				setTotal(data.total);
			} catch (err) {
				setError(err instanceof Error ? err.message : t("adminUsersPage.loadFailed"));
			} finally {
				setLoading(false);
			}
		};

		fetchUsers();
	}, [page, role, search]);

	const handleSearch = (e: React.FormEvent) => {
		e.preventDefault();
		setSearch(searchInput);
		setPage(1);
	};

	const formatDate = (date: string) => {
		return new Date(date).toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
			year: "numeric",
		});
	};

	return (
		<div>
			<div className="page-header">
				<h1>{t('adminNav.users')}</h1>
				<p>{t('adminUsersPage.totalUsers', { count: total })}</p>
			</div>

			<div className="filters-bar">
				<form onSubmit={handleSearch} style={{ flex: 1, display: "flex", gap: "0.5rem" }}>
					<input
						type="text"
						className="search-input"
						placeholder={t('adminChatsPage.searchPlaceholder')}
						value={searchInput}
						onChange={(e) => setSearchInput(e.target.value)}
					/>
					<button type="submit" className="btn btn-outline btn-sm">
						{t('adminChatsPage.search')}
					</button>
				</form>
				<select
					className="filter-select"
					value={role}
					onChange={(e) => {
						setRole(e.target.value);
						setPage(1);
					}}
				>
					<option value="all">{t('adminUsersPage.allRoles')}</option>
					<option value="expert">{t('adminUsersPage.experts')}</option>
					<option value="client">{t('adminUsersPage.clients')}</option>
					<option value="guest">{t('adminUsersPage.guests')}</option>
				</select>
			</div>

			{error && <div className="error-message">{error}</div>}

			<div className="card">
				{loading && users.length === 0 ? (
					<div className="loading">{t('adminUsersPage.loading')}</div>
				) : users.length === 0 ? (
					<div className="empty-state">
						<h3>{t('adminUsersPage.emptyTitle')}</h3>
						<p>{t('adminChatsPage.emptyBody')}</p>
					</div>
				) : (
					<div className="table-container">
						<table>
							<thead>
								<tr>
									<th>{t('adminUsersPage.user')}</th>
									<th>{t('adminUsersPage.role')}</th>
									<th>{t('adminUsersPage.auth')}</th>
									<th>{t('adminUsersPage.rating')}</th>
									<th>{t('nav.subscription')}</th>
									<th>{t('adminUsersPage.joined')}</th>
									<th>{t('adminChatsPage.actions')}</th>
								</tr>
							</thead>
							<tbody>
								{users.map((user) => (
									<tr key={user.id}>
										<td>
											<div style={{ fontWeight: 500 }}>
												{user.displayName ||
													(user.firstName || user.lastName
														? `${user.firstName} ${user.lastName}`.trim()
														: user.email)}
											</div>
											<div style={{ fontSize: "0.75rem", color: "var(--color-gray-500)" }}>
												{user.email}
											</div>
										</td>
										<td>
											<div style={{ display: "flex", gap: "0.25rem", flexWrap: "wrap" }}>
												{user.role === "guest" ? (
													<span className="status-badge" style={{ background: "#f3e8ff", color: "#7c3aed" }}>{t('adminUsersPage.guest')}</span>
												) : (
													<>
														{user.isExpert && (
															<span className="status-badge accepted">{t('adminMediations.expert')}</span>
														)}
														{user.isClient && (
															<span className="status-badge pending">{t('adminMediations.client')}</span>
														)}
													</>
												)}
											</div>
										</td>
										<td>
											<div>
												<span
													style={{
														textTransform: "capitalize",
														fontSize: "0.875rem",
													}}
												>
													{user.authProvider}
												</span>
											</div>
											<div style={{ fontSize: "0.75rem", color: "var(--color-gray-500)" }}>
												{user.role === "guest" ? t('adminUsersPage.noAccount') : (user.emailVerified ? t('adminUsersPage.verified') : t('adminUsersPage.notVerified'))}
											</div>
										</td>
										<td>
											{user.role === "guest" ? (
												<div>
													<span style={{ fontWeight: 500 }}>{user.chatCount || 0}</span>
													<span style={{ fontSize: "0.75rem", color: "var(--color-gray-500)" }}> {t('adminUsersPage.chats')}</span>
												</div>
											) : user.averageRating ? (
												<div>
													<span style={{ fontWeight: 500 }}>
														{user.averageRating.toFixed(1)}
													</span>
													<span
														style={{
															fontSize: "0.75rem",
															color: "var(--color-gray-500)",
														}}
													>
														{" "}
														({user.totalRatings})
													</span>
												</div>
											) : (
												<span style={{ color: "var(--color-gray-400)" }}>{t('adminUsersPage.noRatings')}</span>
											)}
										</td>
										<td>
											{user.role === "guest" ? (
												user.lastActivity ? (
													<div style={{ fontSize: "0.875rem" }}>
														{t('adminUsersPage.lastActivity', { date: formatDate(user.lastActivity) })}
													</div>
												) : (
													<span style={{ color: "var(--color-gray-400)" }}>{t('adminUsersPage.notAvailable')}</span>
												)
											) : user.subscriptionTier ? (
												<div>
													<span
														className={`status-badge ${
															user.subscriptionStatus === "active"
																? "completed"
																: "pending"
														}`}
													>
														{user.subscriptionTier}
													</span>
												</div>
											) : (
												<span style={{ color: "var(--color-gray-400)" }}>{t('adminUsersPage.notAvailable')}</span>
											)}
										</td>
										<td>{formatDate(user.createdAt)}</td>
										<td>
											<div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
												{user.role === "guest" ? (
													<Link to={`/guests/${encodeURIComponent(user.email)}`} className="table-link">
														{t('chatsPage.view')}
													</Link>
												) : (
													<Link to={`/users/${user.id}`} className="table-link">
														{t('chatsPage.view')}
													</Link>
												)}
												{user.storeUrl && (
													<a
														href={user.storeUrl}
														target="_blank"
														rel="noopener noreferrer"
														className="table-link"
													>
														{t('adminUsersPage.store')}
													</a>
												)}
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>

			{totalPages > 1 && (
				<div className="pagination">
					<button
						className="pagination-btn"
						onClick={() => setPage((p) => Math.max(1, p - 1))}
						disabled={page === 1}
					>
						{t('common.previous')}
					</button>
					<span className="pagination-info">
						{t('common.pageOf', { page, totalPages })}
					</span>
					<button
						className="pagination-btn"
						onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
						disabled={page === totalPages}
					>
						{t('common.next')}
					</button>
				</div>
			)}
		</div>
	);
}
